type VideoService = {
    pattern: RegExp;
    match: RegExp;
    replace: (...args: string[]) => string;
};

const services: VideoService[] = [
    {
        pattern: /https:\/\/grain\.com\/(share\/)?highlight\/([a-z0-9-_]{40})(\S+)?/gi,
        match: /^https:\/\/grain\.com\/(share\/)?highlight\/([a-z0-9-_]{40})(\S+)?$/i,
        replace(...args: string[]) {
            const id = args[2];
            const url = `https://grain.com/_/embed/highlight/${id}?show_engagement=false&autoplay=false&origin=user_iframe`;
            return `<iframe title='Grain Recording' src="${url}" width="576" height="364" allow="fullscreen" allow-full-screen="true" frameborder="0"></iframe>`;
        },
    },
    {
        pattern:
            /https:\/\/grain\.com\/(share\/)?story\/(\w{8}-\w{4}-\w{4}-\w{4}-\w{12})\/([a-z0-9-_]{40})(\S+)?/gi,
        match: /^https:\/\/grain\.com\/(share\/)?story\/(\w{8}-\w{4}-\w{4}-\w{4}-\w{12})\/([a-z0-9-_]{40})(\S+)?$/i,
        replace(...args: string[]) {
            const id = args[2];
            const id2 = args[3];
            const url = `https://grain.com/_/embed/story/${id}/${id2}?show_engagement=false&autoplay=false&origin=user_iframe`;
            return `<iframe title='Grain Recording' src="${url}" width="576" height="364" allow="fullscreen" allow-full-screen="true" frameborder="0"></iframe>`;
        },
    },
    {
        pattern:
            /https?:\/\/(www\.)?youtube\.com\/watch\?(?:.*&)?v=([a-z0-9-_]+)(?:&t=((?:\d+h)?(?:\d+m)?(?:\d+s?)))?/gi,
        match: /^https?:\/\/(www\.)?youtube\.com\/watch\?(?:.*&)?v=([a-z0-9-_]+)(?:&t=((?:\d+h)?(?:\d+m)?(?:\d+s?)))?$/i,
        replace(...args: string[]) {
            const id = args[2];
            const t = args[3] || '';

            const parseT = (x: string): number => {
                if (!x) return 0;
                const m = x.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?/i);
                if (!m) return 0;
                return (m[1] ? +m[1] * 3600 : 0) + (m[2] ? +m[2] * 60 : 0) + (m[3] ? +m[3] : 0);
            };

            const start = t ? `?start=${parseT(t)}` : '';
            return `<iframe width="576" height="315" src="https://www.youtube.com/embed/${id}${start}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share" allowfullscreen="true"></iframe>`;
        },
    },
    {
        pattern: /https?:\/\/(www\.)?vimeo\.com\/([0-9]+)(\S*)/gi,
        match: /^https?:\/\/(www\.)?vimeo\.com\/([0-9]+)(\S*)$/i,
        replace(...args: string[]) {
            const id = args[2];
            return `<iframe src="https://player.vimeo.com/video/${id}" width="576" height="360" frameborder="0" allow="fullscreen; picture-in-picture" allowfullscreen="true"></iframe>`;
        },
    },
    {
        pattern: /https?:\/\/fast\.wistia\.net\/embed\/iframe\/(\S*)/gi,
        match: /^https?:\/\/fast\.wistia\.net\/embed\/iframe\/(\S*)$/i,
        replace(...args: string[]) {
            return `<iframe allowtransparency="true" title="Wistia video player" allowfullscreen="true" frameborder="0" scrolling="no" class="wistia_embed" name="wistia_embed" src="${args[0]}" width="576" height="315"></iframe>`;
        },
    },
    {
        pattern: /https?:\/\/www.videoask.com\/([a-z0-9]{40})/gi,
        match: /https?:\/\/www.videoask.com\/([a-z0-9]{40})/i,
        replace(...args: string[]) {
            return `<iframe title="VideoAsk player" allow="autoplay *; fullscreen *; display-capture *; picture-in-picture *" src="${args[0]}" width="100%" height="600" style="border: none; border-radius: 24px"></iframe>`;
        },
    },
];

export function isSupported(url: string): boolean {
    for (const s of services) {
        if (s.match.test(url)) {
            return true;
        }
    }
    return false;
}

export function renderVideos(input: string | null | undefined): string {
    input = input || '';
    const hasHtml = input.includes('<a ');

    // Because of the way the WYSIWYG editor works, when pasting a video link in
    // a field it will get transformed into an <a> html tag.
    // Since previous markdown-based and new HTML-based content have to coexist
    // we need to support both, a raw video link and one inside an <a> tag.
    let replaced = false;
    if (hasHtml) {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(input, 'text/html');
            for (const elem of Array.from(doc.querySelectorAll('a'))) {
                if (
                    elem.href !== elem.textContent.trim() &&
                    elem.href !== `https://${elem.textContent.trim()}`
                ) {
                    continue;
                }

                for (const s of services) {
                    if (s.match.test(elem.href)) {
                        const video = elem.href.replaceAll(s.pattern, s.replace);
                        elem.replaceWith(video);
                        replaced = true;
                        break;
                    }
                }
            }

            if (replaced) {
                input = doc.body.innerHTML
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&amp;/g, '&')
                    .replace(/&nbsp;/g, ' ');
            }
        } catch (e) {
            console.error('error parsing html', e);
        }
    }

    if (!replaced) {
        for (const s of services) {
            input = input.replaceAll(s.pattern, s.replace);
        }
    }

    return input;
}
